/* The session provider and its hooks belong in one module — the hooks are the
 * only supported way to read the provider's context. */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ApiClient } from '../api/client';
import { createResources, type LoginResponse, type Resources } from '../api/resources';
import type { Principal } from '../api/types';
import type { UserRole } from '../api/schema';

/** Where the bearer token is held between reloads. */
export const TOKEN_STORAGE_KEY = 'docsight.session.token';
/** Where the Auth0 refresh token is held between reloads. Only ever set under
 * AUTH_MODE=auth0 — a dev-mode token has no refresh concept. */
export const REFRESH_TOKEN_STORAGE_KEY = 'docsight.session.refresh_token';

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface SessionValue {
  status: SessionStatus;
  /** The resolved principal, or null when not authenticated. */
  principal: Principal | null;
  /** Convenience: the principal's role. */
  role: UserRole | null;
  /**
   * Adopt a bearer token and resolve it against the backend. Resolves the
   * principal on success; on failure the token is dropped and the promise
   * rejects. The BACKEND validates the token — the client never does.
   * ``refreshToken`` is only ever present under AUTH_MODE=auth0.
   */
  signIn: (token: string, refreshToken?: string) => Promise<Principal>;
  /** Drop the token and return to the unauthenticated state. */
  signOut: () => void;
  /**
   * Re-resolve the principal from `/auth/me`. Called after a self-service change
   * (a rename, an avatar upload) so the shell — top bar, profile menu — shows the
   * new value without a reload. A failure is swallowed and the current principal
   * kept: a stale name is a far better outcome than dropping a working session.
   */
  refresh: () => Promise<void>;
  /** True when the principal holds `permission` (server-sourced). */
  hasPermission: (permission: string) => boolean;
  /** The token-attached, 401-aware typed API for the whole app. */
  api: Resources;
}

const SessionContext = createContext<SessionValue | null>(null);

/** Access the session. Throws outside `<SessionProvider>`. */
export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}

/** The current role (or null). Convenience over `useSession().role`. */
export function useRole(): UserRole | null {
  return useSession().role;
}

function readToken(): string | null {
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

function readRefreshToken(): string | null {
  return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

export interface SessionProviderProps {
  children: ReactNode;
  /** Inject a fetch for tests. */
  fetchImpl?: typeof fetch;
  /** Skip the initial token resolution (tests that set state explicitly). */
  skipInitialResolve?: boolean;
  /**
   * Seed an already-resolved principal synchronously (SSR hydration and tests):
   * the provider starts `authenticated` with no round-trip. `initialToken` is the
   * bearer that principal was resolved from.
   */
  initialPrincipal?: Principal | null;
  initialToken?: string;
}

/**
 * Owns the auth session: holds the token, attaches it as a bearer to every API
 * call, resolves the principal via `GET /v1/auth/me`, and on any 401 signs out
 * so the guards redirect to /login. The token lives in localStorage so a reload
 * stays signed in; the principal (role, permissions) always comes fresh from the
 * backend, never decoded from the token client-side.
 */
export function SessionProvider({
  children,
  fetchImpl,
  skipInitialResolve = false,
  initialPrincipal,
  initialToken,
}: SessionProviderProps): React.JSX.Element {
  const seeded = initialPrincipal != null;
  const tokenRef = useRef<string | null>(seeded ? (initialToken ?? 'seeded') : readToken());
  const refreshTokenRef = useRef<string | null>(seeded ? null : readRefreshToken());
  const [status, setStatus] = useState<SessionStatus>(
    seeded ? 'authenticated' : skipInitialResolve || tokenRef.current === null ? 'unauthenticated' : 'loading',
  );
  const [principal, setPrincipal] = useState<Principal | null>(initialPrincipal ?? null);

  const signOut = useCallback(() => {
    tokenRef.current = null;
    refreshTokenRef.current = null;
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    setPrincipal(null);
    setStatus('unauthenticated');
  }, []);

  // `client` must not depend on `renewSession` directly — `renewSession` calls
  // back into `client` to reach `/v1/auth/refresh`, and a `useMemo`/`useCallback`
  // pair can't each depend on the other. A ref indirection breaks the cycle:
  // `client` closes over a stable function that always calls whatever
  // `renewSession` currently is, assigned fresh below on every render.
  const renewSessionImplRef = useRef<() => Promise<string | null>>(() => Promise.resolve(null));

  // One client for the app: reads the live token, tries one silent renewal on
  // a 401 (AUTH_MODE=auth0 only — `renewSession` is a no-op without a stored
  // refresh token), and self-heals by signing out if that fails too.
  const client = useMemo(
    () =>
      new ApiClient({
        fetchImpl,
        getAuthToken: () => tokenRef.current,
        onUnauthorized: () => {
          signOut();
        },
        refreshToken: () => renewSessionImplRef.current(),
      }),
    [fetchImpl, signOut],
  );

  const api = useMemo(() => createResources(client), [client]);

  const renewSession = useCallback(async (): Promise<string | null> => {
    const currentRefresh = refreshTokenRef.current;
    if (currentRefresh === null) return null;
    try {
      const response = await client.post<LoginResponse>('/v1/auth/refresh', {
        refresh_token: currentRefresh,
      });
      tokenRef.current = response.access_token;
      window.localStorage.setItem(TOKEN_STORAGE_KEY, response.access_token);
      if (response.refresh_token !== undefined) {
        refreshTokenRef.current = response.refresh_token;
        window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refresh_token);
      }
      return response.access_token;
    } catch {
      refreshTokenRef.current = null;
      window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      return null;
    }
  }, [client]);
  renewSessionImplRef.current = renewSession;

  const signIn = useCallback(
    async (token: string, refreshToken?: string): Promise<Principal> => {
      tokenRef.current = token;
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
      if (refreshToken !== undefined) {
        refreshTokenRef.current = refreshToken;
        window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      }
      setStatus('loading');
      try {
        const resolved = await api.auth.me();
        setPrincipal(resolved);
        setStatus('authenticated');
        return resolved;
      } catch (error) {
        signOut();
        throw error;
      }
    },
    [api, signOut],
  );

  const refresh = useCallback(async (): Promise<void> => {
    if (tokenRef.current === null) return;
    try {
      setPrincipal(await api.auth.me());
    } catch {
      // Deliberately silent — see the doc comment on `refresh`. A 401 has
      // already signed the session out through the client's own handler.
    }
  }, [api]);

  // On mount, resolve an existing token (a returning session).
  useEffect(() => {
    if (skipInitialResolve || seeded) return;
    const token = tokenRef.current;
    if (token === null) return;
    const live = { current: true };
    void (async () => {
      try {
        const resolved = await api.auth.me();
        if (live.current) {
          setPrincipal(resolved);
          setStatus('authenticated');
        }
      } catch {
        if (live.current) signOut();
      }
    })();
    return () => {
      live.current = false;
    };
  }, [api, signOut, skipInitialResolve, seeded]);

  const hasPermission = useCallback(
    (permission: string) => principal?.permissions.includes(permission) ?? false,
    [principal],
  );

  const value = useMemo<SessionValue>(
    () => ({
      status,
      principal,
      role: principal?.role ?? null,
      signIn,
      signOut,
      refresh,
      hasPermission,
      api,
    }),
    [status, principal, signIn, signOut, refresh, hasPermission, api],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
